import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { type ColumnDef } from "@tanstack/react-table";
import { IconChevronRight, IconFolder } from "@tabler/icons-react";

import PageHeader from "@/components/common/PageHeader";
import { DataTable } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { DashboardCard, StatusText } from "@/components/dashboard/dashboard-card";
import { listBrandContentPrograms, type ContentProgramBrandRow } from "@/services/contentProgram.service";
import { formatNumber } from "@/utils/formatters";

const shortDate = (value: string) =>
  new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" });

export default function BrandContentProgramsList() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["brand-content-programs"],
    queryFn: listBrandContentPrograms,
  });

  const columns = useMemo<ColumnDef<ContentProgramBrandRow>[]>(
    () => [
      {
        id: "title",
        header: "Program",
        cell: ({ row }) => (
          <span className="flex min-w-0 items-center gap-2.5">
            <span className="flex size-7 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground">
              <IconFolder size={14} stroke={1.75} aria-hidden />
            </span>
            <span className="block max-w-[20rem] truncate font-medium text-foreground" title={row.original.title}>
              {row.original.title}
            </span>
          </span>
        ),
      },
      {
        id: "commission",
        header: "Commission",
        meta: { fit: true },
        cell: ({ row }) => <span className="tabular-nums text-foreground">{row.original.commissionRate}%</span>,
      },
      {
        id: "submissions",
        header: "Submissions",
        meta: { fit: true },
        cell: ({ row }) => (
          <span className="tabular-nums text-foreground">{formatNumber(row.original._count?.contents ?? 0)}</span>
        ),
      },
      {
        id: "affiliates",
        header: "Affiliates",
        meta: { fit: true },
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-foreground">
            {row.original.isOpenToAllEnrolledAffiliates
              ? "All enrolled"
              : formatNumber(row.original._count?.allowedAffiliates ?? 0)}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        meta: { fit: true },
        cell: ({ row }) => <StatusText status={row.original.status} />,
      },
      {
        id: "created",
        header: "Created",
        meta: { fit: true },
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">{shortDate(row.original.createdAt)}</span>
        ),
      },
      {
        id: "open",
        header: "",
        meta: { fit: true, cellClassName: "text-right", skeleton: <span /> },
        cell: () => <IconChevronRight size={16} stroke={1.75} className="inline-block text-muted-foreground" aria-hidden />,
      },
    ],
    []
  );

  const rows = data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Content programs"
        customActions={[
          {
            id: "new-content-program",
            label: "New content program",
            variant: "primary",
            onClick: () => navigate("/brand/content-programs/new"),
          },
        ]}
      />

      <DashboardCard title="Programs" bodyClassName="px-3 pb-3">
        {!isLoading && rows.length === 0 ? (
          <EmptyState
            bordered={false}
            icon={<IconFolder stroke={1.75} />}
            title="No content programs yet"
            body="A program collects creative from affiliates that you can run as Meta ads"
          />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            isLoading={isLoading}
            skeletonRows={4}
            getRowId={(row) => row.id}
            onRowClick={(row) => navigate(`/brand/content-programs/${row.id}`)}
            emptyText="No content programs yet"
          />
        )}
      </DashboardCard>
    </div>
  );
}
